import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useReports } from './ReportsContext';
import { 
  Card, Row, Col, Button, Spin, Table, DatePicker, Empty, Statistic, Alert, 
  Space, Divider, Progress, Typography, Tag, List, Tooltip, message 
} from 'antd';
import { 
  DownloadOutlined, CalendarOutlined, FileExcelOutlined, FileDoneOutlined, 
  UploadOutlined, BarChartOutlined, PieChartOutlined,
  CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined,
  CarOutlined, ClockCircleOutlined, ScheduleOutlined, LinkOutlined,
  WarningOutlined, FieldTimeOutlined
} from '@ant-design/icons';
import { useLocation, useHistory } from 'react-router-dom';
import dayjs from '../../dayjs';
import { generateOTRReport, getTimingStatus } from './utils/OTRAnalytics';
import OTRReport from './components/OTRReport';
import Papa from 'papaparse';
import { Storage } from 'aws-amplify';

const { RangePicker } = DatePicker;
const { Title, Text, Paragraph } = Typography;

// Time periods classification for analytics
const TIME_PERIODS = {
  MORNING: { start: '06:00', end: '10:00', label: 'Morning Peak' },
  MIDDAY: { start: '10:01', end: '15:00', label: 'Midday' },
  AFTERNOON: { start: '15:01', end: '19:00', label: 'Afternoon Peak' },
  EVENING: { start: '19:01', end: '23:59', label: 'Evening' },
  NIGHT: { start: '00:00', end: '05:59', label: 'Night' },
};

// Helper function to determine time period from a time string
const getTimePeriod = (timeString) => {
  if (!timeString) return 'unknown';
  
  try {
    // Extract time part from datetime string
    let timePart = timeString;
    if (timeString.includes(' ')) {
      timePart = timeString.split(' ')[1];
    }
    
    // Default to empty string if splitting fails
    if (!timePart) return 'unknown';
    
    // Extract hours:minutes
    const timeForComparison = timePart.substring(0, 5);
    
    for (const [period, { start, end }] of Object.entries(TIME_PERIODS)) {
      if (timeForComparison >= start && timeForComparison <= end) {
        return period;
      }
    }
    
    return 'unknown';
  } catch (error) {
    console.warn('Error determining time period:', error);
    return 'unknown';
  }
};

// Safe implementation of URL params parsing that works in all browsers
const parseQueryParams = (search) => {
  try {
    // Modern browsers support URLSearchParams
    const params = new URLSearchParams(search);
    return {
      startDate: params.get('startDate'),
      endDate: params.get('endDate')
    };
  } catch (e) {
    // Fallback for older browsers
    console.warn('URLSearchParams not supported, using regex fallback');
    const startDateMatch = search.match(/[?&]startDate=([^&]*)/);
    const endDateMatch = search.match(/[?&]endDate=([^&]*)/);
    return {
      startDate: startDateMatch ? decodeURIComponent(startDateMatch[1]) : null,
      endDate: endDateMatch ? decodeURIComponent(endDateMatch[1]) : null
    };
  }
};

export default function OTR() {
  const {
    reports = [],
    isLoading,
    downloadReport,
    filters,
    updateFilters,
    getReportUrl
  } = useReports();
  
  const location = useLocation();
  const history = useHistory();
  
  // Memoize queryParams to prevent recalculation on every render
  const queryParams = useMemo(() => 
    parseQueryParams(location.search),
    [location.search]
  );

  // Filter for OTR reports only, safely handling non-array reports
  const otrReports = Array.isArray(reports) 
    ? reports.filter(report => report.reportType === 'otr')
    : [];
  
  // Date range filter state
  const [dateRange, _setDateRange] = useState(null);
  const setDateRange = (range) => {
    if (Array.isArray(range) && range.length === 2 && range[1]) {
      _setDateRange([range[0], range[1].endOf('day')]);
    } else {
      _setDateRange(range);
    }
  };
  const [selectedReports, setSelectedReports] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [processingReport, setProcessingReport] = useState(false);
  const [reportError, setReportError] = useState(null);
  
  // Flag to track if initial URL params have been processed
  const [initialParamsProcessed, setInitialParamsProcessed] = useState(false);
  
  // Read date range from URL params on component mount
  useEffect(() => {
    if (location.search && !initialParamsProcessed) {
      const { startDate, endDate } = queryParams;
      
      if (startDate && endDate) {
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        
        if (start.isValid() && end.isValid()) {
          setDateRange([start, end]);
          setInitialParamsProcessed(true);
        }
      }
    }
  }, [location.search, queryParams, initialParamsProcessed]);
  
  // Memoize the updateUrlParams function
  const updateUrlParams = useCallback((start, end) => {
    if (start && end) {
      const params = new URLSearchParams();
      params.set('startDate', start.format('YYYY-MM-DD'));
      params.set('endDate', end.format('YYYY-MM-DD'));
      
      const searchString = params.toString();
      // Only update if the search params would actually change
      if (searchString !== location.search.replace(/^\?/, '')) {
        history.push({
          pathname: location.pathname,
          search: searchString
        });
      }
    } else if (location.search) {
      // Only clear if there are params to clear
      history.push({
        pathname: location.pathname,
        search: ''
      });
    }
  }, [history, location.pathname, location.search]);
  
  // Update URL when date range changes (separate from filters update)
  useEffect(() => {
    // Skip during initial URL params processing to avoid URL change loop
    if (initialParamsProcessed || (!location.search && dateRange)) {
      if (dateRange && dateRange[0] && dateRange[1]) {
        updateUrlParams(dateRange[0], dateRange[1]);
      } else if (location.search) {
        updateUrlParams(null, null);
      }
    }
  }, [dateRange, updateUrlParams, initialParamsProcessed, location.search]);
  
  // Update filters when date range changes
  useEffect(() => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      updateFilters({ 
        dateRange: { 
          start: dateRange[0].startOf('day'),
          end: dateRange[1].endOf('day')
        },
        reportType: 'otr'
      });
    } else {
      updateFilters({ 
        dateRange: null,
        reportType: 'otr'
      });
    }
  }, [dateRange, updateFilters]);
  
  // Auto-generate report when URL params are processed
  useEffect(() => {
    if (initialParamsProcessed && dateRange && getReportUrl && !reportData && !processingReport) {
      generateReport();
    }
  }, [initialParamsProcessed, dateRange, getReportUrl, reportData, processingReport]);
  // Intentionally omitting reportData and processingReport to prevent regenerating on render


  // Get the original files from selected reports
  const getSelectedReportFiles = async () => {
    console.log('Getting selected reports for date range:', dateRange);
    const selectedReports = getFilteredReports();
    
    if (!selectedReports || selectedReports.length === 0) {
      console.warn('No reports selected');
      return [];
    }
    
    // Fetch the actual files from storage
    try {
      // Use Promise.all to fetch all files in parallel
      const reportFilesPromises = selectedReports
        .filter(report => report.fileKey) // Filter out reports without fileKey
        .map(async (report) => {
          console.log(`Processing report: ${report.reportId}, file key: ${report.fileKey}`);
          
          try {
            // Get the file using our simplified helper function
            const url = await getReportUrl(report);
            if (!url) {
              console.error(`Could not access file for report ${report.reportId}`);
              return null;
            }
            
            console.log(`Got URL for file: ${url}`);
            
            // Fetch the file content
            console.log(`Fetching file content...`);
            const response = await fetch(url).catch(e => {
              console.error(`Fetch error:`, e);
              throw e;
            });
            
            const blob = await response.blob();
            // Create a File object from the blob
            const file = new File([blob], report.fileName || `report-${report.reportId}.csv`, { 
              type: report.fileType === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv' 
            });
            
            return file;
          } catch (error) {
            console.error(`Error fetching file for report ${report.reportId}:`, error);
            return null;
          }
        });
      
      // Wait for all promises to resolve and filter out null results
      const reportFiles = (await Promise.all(reportFilesPromises)).filter(file => file !== null);
      
      return reportFiles;
    } catch (error) {
      console.error('Error fetching report files:', error);
      return [];
    }
  };

  // Filter reports by date range (safely)
  const getFilteredReports = () => {
    if (!Array.isArray(otrReports)) return [];
    
    if (dateRange && dateRange[0] && dateRange[1]) {
      return otrReports.filter(r => {
        if (!r.reportDates?.length) return false;
        const reportDates = r.reportDates.map(d => dayjs(d, 'YYYY-MM-DD'));
        return reportDates.some(d => d.isValid() && 
          d.isBetween(dateRange[0], dateRange[1], null, '[]'));
      });
    }
    
    return otrReports;
  };

  // Get statistics for the current filtered reports
  const getStats = () => {
    const filteredReports = getFilteredReports();
    return {
      total: filteredReports.length,
      totalSize: filteredReports.reduce((sum, report) => sum + (report.fileSize || 0), 0),
      dateRange: dateRange ? {
        start: dateRange[0].format('YYYY-MM-DD'),
        end: dateRange[1].format('YYYY-MM-DD')
      } : null
    };
  };

  // Format total size
  const formatTotalSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
  };

  // Generate report from selected files
  const generateReport = async () => {
    setProcessingReport(true);
    setReportData(null);
    setReportError(null);
    
    try {
      console.log('Starting report generation...');
      
      // Get reports for the selected date range
      const filteredReports = getFilteredReports();
      if (!filteredReports.length) {
        // Instead of throwing an error, set a friendly message and return early
        setReportData({
          summary: {
            noData: true,
            message: 'No data available for the selected date range'
          }
        });
        return; // Exit function early
      }
      
      console.log(`Processing ${filteredReports.length} reports in date range:`, dateRange);
      
      // Get files for ALL reports in the date range, not just selected ones
      setSelectedReports(filteredReports); // Update selected reports for consistency
      
      // Get files for the reports
      const files = await getSelectedReportFiles();
      if (!files || !files.length) {
        setReportData({
          summary: {
            noData: true,
            message: 'No file data available for the selected reports'
          }
        });
        return; // Exit function early
      }
      
      console.log(`Generated ${files.length} sample files for testing`);
      
      // Parse CSV files in parallel
      const fileParsingPromises = files.map(async (file) => {
        try {
          console.log(`Parsing file: ${file.name}`, file);
          
          // Read file content
          const text = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.readAsText(file);
          });
          
          // Basic validation of CSV content
          if (!text || text.length < 100) {
            console.warn(`File ${file.name} has insufficient content:`, text);
            return null;
          }
          
          // Log the first few lines of the CSV for debugging
          console.log('CSV preview (first 200 chars):', text.substring(0, 200));
          
          // Parse CSV - correctly handling the header row
          const parseOptions = {
            delimiter: ";", // Using semicolon as delimiter for standard OTR format
            header: true,  // First row is the header
            skipEmptyLines: true,
            transformHeader: h => h.trim(),
            transform: v => v.trim()
          };
          
          const result = Papa.parse(text, parseOptions);

          console.log('result', result);
          
          if (result.errors && result.errors.length > 0) {
            console.warn(`Parse errors in ${file.name}:`, result.errors);
          }
          
          if (!result.data || !result.data.length) {
            console.warn(`No data parsed from ${file.name}`);
            return null;
          }
          
          console.log(`Successfully parsed ${result.data.length} records from ${file.name}`);
          console.log('First row from parsed CSV (raw):', result.data[0],result.data[1]);
          
          // Helper functions for data transformation
          const getStopPosition = (row) => {
            // Use TSN_FIRST_LAST_IND if available (preferred method)
            if (row.TSN_FIRST_LAST_IND) {
              if (row.TSN_FIRST_LAST_IND === 'F') return 'first';
              if (row.TSN_FIRST_LAST_IND === 'L') return 'last';
              return 'middle';
            }
            
            // Fallback to sequence-based detection
            if (row.TIME_RECORD_SEQUENCE === '1') return 'first';
            if (row.STOP_TYPE === '1' || row.STOP_SEQ === '1' || row.SEQ_NO === '1') return 'first';
            if (row.STOP_TYPE === '5' || row.STOP_SEQ === '5' || (row.SEQ_NO && parseInt(row.SEQ_NO) > 3)) return 'last';
            
            return 'middle';
          };
          
          // Map data to object structure expected by analysis functions
          const transformedData = result.data.map(row => {
            // Skip if missing essential data
            if (!row) return null;
            
            // Calculate the variance in seconds for consistency
            let varianceInSeconds = null; // Start with null to indicate no variance data
            let hasVarianceData = false;
            
            try {
              // Check for variance data in the correct columns
              const varianceStr = row.ARRIVAL_TIME_VARIANCE || row.DEPARTURE_TIME_VARIANCE || row.VARIANCE_SECS || row.VARIANCE;
              
              // Only process if we have actual variance data
              if (varianceStr && varianceStr.trim() !== '') {
                varianceInSeconds = parseInt(varianceStr, 10);
                hasVarianceData = true;
                
                // Log large variances for inspection
                if (Math.abs(varianceInSeconds) > 300) {
                  console.log(`Large variance found: ${varianceInSeconds} seconds (${varianceInSeconds/60} minutes) for trip ${row.TRIP_ID}, route ${row.ROUTE || row.ROUTE_NO}`);
                }
                
                if (isNaN(varianceInSeconds)) {
                  console.warn(`Invalid variance value: ${varianceStr}, setting to null`);
                  varianceInSeconds = null;
                  hasVarianceData = false;
                }
              } else {
                // No variance data available
                console.log(`No variance data for trip ${row.TRIP_ID}, route ${row.ROUTE || row.ROUTE_NO}`);
              }
            } catch (e) {
              console.warn(`Error parsing variance: ${e.message}, setting to null`);
              varianceInSeconds = null;
              hasVarianceData = false;
            }
            
            // Determine timing status based on variance (only if we have variance data)
            const timingStatus = hasVarianceData ? getTimingStatus(varianceInSeconds) : 'unknown';
            
            // Get TSN value - priority to raw TSN field, then alternatives
            const tsnValue = row.TSN || row.MASTER_STOP_NO || row.STOP_ID || row.STOP_CODE || '';
            
            // Debug logging for TSN
            if (tsnValue) {
              console.log(`Found TSN value: ${tsnValue} for trip ${row.TRIP_ID}`);
            } else {
              console.warn(`Missing TSN for trip ${row.TRIP_ID}, row keys: ${Object.keys(row).join(', ')}`);
            }
            
            // Extract all useful data from the row with standardized field names
            return {
              date: row.SCHEDULED_CALENDAR_DATE || row.RUN_DATE || row.TRIP_DATE || row.REPORT_DATE || '',
              tripId: row.TRIP_ID || '',
              routeNo: row.ROUTE || row.ROUTE_NO || '', // Use routeNo instead of routeId to match analytics expectations
              stopId: tsnValue, // Use the TSN value we extracted above
              stopName: row.MASTER_STOP_NAME || row.STOP_NAME || '',
              scheduledTime: row.SCHEDULED_DEPARTURE_TIME || row.SCHEDULED_ARRIVAL_TIME || row.SCHEDULED_DEPT_TIME || row.SCHEDULED_TIME || row.SCHEDULED_ARR_TIME || '',
              actualTime: row.ACTUAL_DEPARTURE_TIME || row.ACTUAL_ARRIVAL_TIME || row.ACTUAL_DEPT_TIME || row.ACTUAL_ARR_TIME || row.ACTUAL_TIME || '',
              actualArrivalTime: row.ACTUAL_ARRIVAL_TIME || '',
              actualDepartureTime: row.ACTUAL_DEPARTURE_TIME || '',
              varianceInSeconds: varianceInSeconds,
              stopPosition: getStopPosition(row).toLowerCase(), // Ensure lowercase for analytics
              driverId: row.DRIVER_ID || '',
              driverType: row.DRIVER_TYPE || '',
              isTimingPoint: row.IS_TIMING_POINT === '1' || row.IS_TIMING_POINT === 'true' || row.TIMING_POINT === '1' || row.TIMING_POINT === 'true',
              direction: row.DIRECTION || '',
              // Include other fields that might be used by analytics
              busId: row.VEHICLE_ID || row.BUS_ID || '',
              runId: row.RUN_ID || '',
              depotId: row.DEPOT || row.DEPOT_ID || '',
              regionId: row.DATA_DOMAIN || row.REGION_ID || '',
              regionName: row.REGION_NAME || '',
              tripDate: row.SCHEDULED_CALENDAR_DATE || row.TRIP_DATE || row.RUN_DATE || row.REPORT_DATE || '',
              // Explicitly include raw TSN and TIME_RECORD_SEQUENCE from TPT file
              TSN: tsnValue, // Use the same value we extracted above
              TIME_RECORD_SEQUENCE: row.TIME_RECORD_SEQUENCE || '',
              TSN_FIRST_LAST_IND: row.TSN_FIRST_LAST_IND || '',
              // Add computed fields used by analytics
              timingStatus: timingStatus,
              timePeriod: getTimePeriod(row.SCHEDULED_DEPARTURE_TIME || row.SCHEDULED_ARRIVAL_TIME || row.SCHEDULED_DEPT_TIME || row.SCHEDULED_TIME || row.SCHEDULED_ARR_TIME || ''),
              // Flag to indicate if this record has valid variance data
              hasVarianceData: hasVarianceData,
              // Add untracked field - false if ACTUAL_DEPARTURE_TIME is NaN, empty, or empty string
              tracked: row.ACTUAL_DEPARTURE_TIME?.trim().length || row.ACTUAL_ARRIVAL_TIME?.trim().length
            };
          }).filter(Boolean); // Remove any null items
          
          console.log(`Transformed ${transformedData.length} records from ${file.name}`);
          if (transformedData.length > 0) {
            console.log('First transformed record:', transformedData[0]);
          }
          
          return transformedData;
        } catch (error) {
          console.error(`Error parsing file ${file.name}:`, error);
          return null;
        }
      });
      
      // Wait for all parsing to complete and filter out null results
      const parsedResults = (await Promise.all(fileParsingPromises)).filter(result => result !== null);
      
      // Combine all parsed data
      const parsedData = parsedResults.flat();
      
      if (parsedData.length === 0) {
        throw new Error('No valid data found in the selected report files');
      }
      
      console.log(`Total records parsed: ${parsedData.length}`);
      
      // Detailed check of parsed data
      const fieldCounts = {
        tripId: parsedData.filter(r => r.tripId).length,
        routeNo: parsedData.filter(r => r.routeNo).length,
        stopId: parsedData.filter(r => r.stopId).length,
        varianceInSeconds: parsedData.filter(r => r.varianceInSeconds !== undefined).length,
        timingStatus: parsedData.filter(r => r.timingStatus).length,
        timePeriod: parsedData.filter(r => r.timePeriod).length,
        stopPosition: parsedData.filter(r => r.stopPosition).length,
        driverId: parsedData.filter(r => r.driverId).length,
        untracked: parsedData.filter(r => r.untracked).length
      };
      console.log('Field presence in parsed data:', fieldCounts);
      // Generate the report
      const reportResult = generateOTRReport(parsedData);
      
      console.log('Report result:', {
        hasError: !!reportResult.error,
        errorMsg: reportResult.error || 'None',
        summaryStats: reportResult.summary || 'None',
        analysisCount: reportResult.analysis ? Object.keys(reportResult.analysis).length : 0,
        routePerformanceCount: reportResult.routePerformance && reportResult.routePerformance.routes ? 
          reportResult.routePerformance.routes.length : 0,
        timeOfDayAnalysisCount: reportResult.timeOfDayAnalysis ? Object.keys(reportResult.timeOfDayAnalysis).length : 0
      });
      
      if (reportResult.error) {
        throw new Error(`Analysis error: ${reportResult.error}`);
      }
      
      // Add the parsed data to the report result object
      reportResult.parsedData = parsedData;
      
      console.log('Report generation successful:', reportResult);
      
      // Set the report data
      setReportData(reportResult);
    } catch (error) {
      console.error('Report generation failed:', error);
      setReportError(`${error.message}${error.stack ? '\n\nStack: ' + error.stack : ''}`);
    } finally {
      setProcessingReport(false);
    }
  };

  // Format percentage for display
  const formatPercent = (value) => {
    if (value === undefined || value === null) return '0%';
    return `${value.toFixed(1)}%`;
  };

  const stats = getStats();

  // Create a sharable link
  const getShareableLink = () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return null;
    
    const url = new URL(window.location.href);
    url.searchParams.set('startDate', dateRange[0].format('YYYY-MM-DD'));
    url.searchParams.set('endDate', dateRange[1].format('YYYY-MM-DD'));
    return url.toString();
  };

  // Copy link to clipboard
  const copyLinkToClipboard = () => {
    const link = getShareableLink();
    if (link) {
      try {
        navigator.clipboard.writeText(link);
        message.success('Link copied to clipboard!');
      } catch (e) {
        // Fallback for browsers without clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = link;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          if (successful) {
            message.success('Link copied to clipboard!');
          } else {
            message.warning('Failed to copy link. Try again or copy URL manually.');
          }
        } catch (err) {
          message.error('Failed to copy link: ' + err);
        }
        document.body.removeChild(textArea);
      }
    }
  };

  // Handle clicking a preset date range
  const handlePresetRange = (days) => {
    const end = dayjs();
    let start;
    
    switch(days) {
      case 1: // Last 24 hours
        start = end.subtract(1, 'day');
        break;
      case 7: // Last 7 days
        start = end.subtract(7, 'day');
        break;
      case 30: // Last 30 days
        start = end.subtract(30, 'day');
        break;
      case 365: // Last year
        start = end.subtract(1, 'year');
        break;
      default:
        start = end.subtract(7, 'day');
    }
    
    setDateRange([start, end]);
  };
  
  // Determine which preset is active
  const getActivePreset = () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return null;
    
    const start = dateRange[0];
    const end = dateRange[1];
    const now = dayjs();
    
    // If end date is today or yesterday (for last 24h)
    if (end.isSame(now, 'day') || 
        end.isSame(now.subtract(1, 'day'), 'day')) {
        
      // Check for each preset
      if (start.isSame(now.subtract(1, 'day'), 'day')) return 1;
      if (start.isSame(now.subtract(7, 'day'), 'day')) return 7;
      if (start.isSame(now.subtract(30, 'day'), 'day')) return 30;
      if (start.isSame(now.subtract(1, 'year'), 'day')) return 365;
    }
    
    return null; // Custom range
  };

  // Add handlers for the "no data" case
  const handleRetry = () => {
    // Clear existing data and regenerate report
    setReportData(null);
    setReportError(null);
    generateReport();
  };
  
  const handleChangeDateRange = () => {
    // Just focus on the date picker
    const datePickerElement = document.querySelector('.ant-picker-input input');
    if (datePickerElement) {
      datePickerElement.focus();
    }
  };

  const styles = {
    container: {
      padding: '20px',
    },
    header: {
      marginBottom: '20px',
    },
    dateRangeContainer: {
      display: 'flex',
      gap: '10px',
      marginBottom: '20px',
      flexWrap: 'wrap',
    },
    quickSelectContainer: {
      display: 'flex',
      gap: '10px',
      marginBottom: '20px',
      flexWrap: 'wrap',
    },
    summaryStats: {
      marginTop: '20px',
      padding: '20px',
      backgroundColor: '#f5f5f5',
      borderRadius: '8px',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '15px',
      marginTop: '15px',
    },
    statItem: {
      display: 'flex',
      flexDirection: 'column',
      padding: '10px',
      backgroundColor: 'white',
      borderRadius: '4px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    statLabel: {
      fontSize: '14px',
      color: '#666',
      marginBottom: '5px',
    },
    statValue: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#333',
    },
  };

  return (
    <div className="otr-container">
      <Title level={3}>On-Time Running (OTR) Analysis</Title>
      
      {/* Show loading state when reports data is being fetched */}
      {isLoading ? (
        <Card style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 20 }}>
            <Title level={4}>Loading Data</Title>
            <Text type="secondary">Retrieving available data files...</Text>
          </div>
        </Card>
      ) : (
        <>
          <Card title="Data Analysis Controls" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={24}>
                <Space size="small" style={{ marginBottom: 16 }}>
                  <Text strong>Quick Ranges:</Text>
                  <Button 
                    type={getActivePreset() === 1 ? "primary" : "default"}
                    size="small" 
                    onClick={() => handlePresetRange(1)}
                  >
                    Last 24h
                  </Button>
                  <Button 
                    type={getActivePreset() === 7 ? "primary" : "default"}
                    size="small" 
                    onClick={() => handlePresetRange(7)}
                  >
                    Last 7 days
                  </Button>
                  <Button 
                    type={getActivePreset() === 30 ? "primary" : "default"}
                    size="small" 
                    onClick={() => handlePresetRange(30)}
                  >
                    Last 30 days
                  </Button>
                  <Button 
                    type={getActivePreset() === 365 ? "primary" : "default"}
                    size="small" 
                    onClick={() => handlePresetRange(365)}
                  >
                    Last year
                  </Button>
                </Space>
              </Col>
            </Row>
            
            <Row gutter={16} align="middle">
              <Col xs={24} md={16}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong style={{ fontSize: 16 }}>Select Date Range for Analysis</Text>
                  <RangePicker 
                    value={dateRange}
                    onChange={setDateRange}
                    allowClear
                    placeholder={['Start Date', 'End Date']}
                    style={{ width: '100%', marginBottom: 8 }}
                    size="large"
                  />
                  <Text type="secondary">
                    All data within the selected date range will be included in the analysis.
                  </Text>
                </Space>
              </Col>
              <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                <Button 
                  type="primary" 
                  onClick={generateReport}
                  disabled={!dateRange || !dateRange[0] || !dateRange[1] || !getFilteredReports().length}
                  icon={<BarChartOutlined />}
                  loading={processingReport}
                  size="large"
                  style={{ height: 50, fontSize: 16, width: '100%' }}
                >
                  Generate OTR Report
                </Button>
              </Col>
            </Row>
            
            {/* Shareable link row */}
            {dateRange && dateRange[0] && dateRange[1] && (
              <Row style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Space>
                    <InfoCircleOutlined />
                    <Text>
                      Data range: <strong>{dateRange[0].format('YYYY-MM-DD')} to {dateRange[1].format('YYYY-MM-DD')}</strong>
                    </Text>
                    <Divider type="vertical" />
                    <Text>
                      Available files: <strong>{getFilteredReports().length}</strong>
                    </Text>
                    <Divider type="vertical" />
                    <Button 
                      type="link" 
                      size="small" 
                      onClick={copyLinkToClipboard}
                      icon={<LinkOutlined />}
                    >
                      Copy Shareable Link
                    </Button>
                  </Space>
                </Col>
              </Row>
            )}
          </Card>
          
          {/* Analytics content - directly shown when report data exists */}
          {processingReport && (
            <Card style={{ textAlign: 'center', padding: '20px 0' }}>
              <Spin size="large" tip="Generating OTR Report..." />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Analyzing data from {getFilteredReports().length} files...</Text>
              </div>
            </Card>
          )}
          
          {!processingReport && reportData && (
            <>
              <OTRReport 
                reportData={reportData}
                dateRange={dateRange}
                loading={processingReport}
                error={reportError}
                onRetry={handleRetry}
                onChangeDateRange={handleChangeDateRange}
              />
              
              {reportData.summary && (
                <div style={styles.summaryStats}>
                  <h3>Summary Statistics</h3>
                  <div style={styles.statsGrid}>
                    <div style={styles.statItem}>
                      <span style={styles.statLabel}>Total Records:</span>
                      <span style={styles.statValue}>{reportData.summary.totalRecords || 0}</span>
                    </div>
                    <div style={styles.statItem}>
                      <span style={styles.statLabel}>Records with Variance Data:</span>
                      <span style={styles.statValue}>{reportData.summary.recordsWithVarianceData || 0}</span>
                    </div>
                    <div style={styles.statItem}>
                      <span style={styles.statLabel}>Records without Variance Data:</span>
                      <span style={styles.statValue}>{reportData.summary.recordsWithoutVarianceData || 0}</span>
                    </div>
                    <div style={styles.statItem}>
                      <span style={styles.statLabel}>Untracked Records:</span>
                      <span style={styles.statValue}>{reportData.summary.untrackedRecords || 0}</span>
                    </div>
                    <div style={styles.statItem}>
                      <span style={styles.statLabel}>On Time:</span>
                      <span style={styles.statValue}>
                        {typeof reportData.summary.onTimePercentage === 'number' 
                          ? `${reportData.summary.onTimePercentage.toFixed(1)}%`
                          : '0.0%'}
                      </span>
                    </div>
                    <div style={styles.statItem}>
                      <span style={styles.statLabel}>Early:</span>
                      <span style={styles.statValue}>
                        {typeof reportData.summary.earlyPercentage === 'number'
                          ? `${reportData.summary.earlyPercentage.toFixed(1)}%`
                          : '0.0%'}
                      </span>
                    </div>
                    <div style={styles.statItem}>
                      <span style={styles.statLabel}>Late:</span>
                      <span style={styles.statValue}>
                        {typeof reportData.summary.latePercentage === 'number'
                          ? `${reportData.summary.latePercentage.toFixed(1)}%`
                          : '0.0%'}
                      </span>
                    </div>
                    <div style={styles.statItem}>
                      <span style={styles.statLabel}>Very Late:</span>
                      <span style={styles.statValue}>
                        {typeof reportData.summary.veryLatePercentage === 'number'
                          ? `${reportData.summary.veryLatePercentage.toFixed(1)}%`
                          : '0.0%'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          
          {!processingReport && reportError && (
            <Alert
              message="Error Generating Report"
              description={reportError}
              type="error"
              showIcon
            />
          )}
          
          {!processingReport && !reportData && !reportError && dateRange && dateRange[0] && dateRange[1] && (
            <Card style={{ textAlign: 'center', padding: '30px 0' }}>
              <Empty
                description={
                  <span>
                    No report generated yet. Click "Generate OTR Report" to analyze data.
                  </span>
                }
              />
            </Card>
          )}
          
          {!dateRange && (
            <Card style={{ textAlign: 'center', padding: '30px 0' }}>
              <Empty
                description={
                  <span>
                    Please select a date range to begin analysis.
                  </span>
                }
              />
            </Card>
          )}
        </>
      )}
    </div>
  );
}
